import React, { useState, useEffect } from 'react';
import { useLocation } from "react-router-dom";
import { Box, Typography} from '@mui/material';
import { styled } from '@mui/system';


const AllUsers = () => {
  const location = useLocation();
  // Clicking a "New admin/super_admin registered" notification (see
  // notificationsPage.js) navigates here with location.state.autoOpenUserId
  // — this opens that user's details modal once the list has loaded.
  // Consumed once so it doesn't reopen if the user closes the modal and
  // something else re-renders this component.
  const autoOpenConsumedRef = React.useRef(false);

  const [allUsers, setAllUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editedUser, setEditedUser] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [viewingUser, setViewingUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userAddresses, setUserAddresses] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [viewedUserIds, setViewedUserIds] = useState(() => {
    try {
      const stored = localStorage.getItem('viewedNewUserIds');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  const [snackbar, setSnackbar] = useState({
    show: false,
    message: '',
    type: 'success'
  });
  const [countries, setCountries] = useState([]); // { countryId, countryName, countryPhoneCode, isoCode }

  useEffect(() => {
    const targetUserId = location.state?.autoOpenUserId;
    if (!targetUserId || autoOpenConsumedRef.current) return;
    if (allUsers.length === 0) return; // list hasn't loaded yet — try again once it does

    const match = allUsers.find((u) => String(u.userId) === String(targetUserId));
    if (!match) return; // loaded but not found (e.g. deleted) — nothing to open

    autoOpenConsumedRef.current = true;
    handleViewClick(match);
  }, [location.state, allUsers]);

  const SpinningLogo = styled("img")(({ theme }) => ({
  width: "150px",
  height: "150px",
}));


  // Mobile number validation function
  const validateMobileNumber = (number) => {
    // Remove all non-digit characters
    const digitsOnly = number.replace(/\D/g, '');
    // Check if it's between 10 and 15 digits and contains only numbers
    return /^\d{10,15}$/.test(digitsOnly);
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ show: true, message, type });
    setTimeout(() => {
      setSnackbar({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  const formatCountryCode = (countryCode) => {
    if (!countryCode) return '-';
    const cleanCode = countryCode.toString().replace(/^\+/, '');
    return cleanCode ? `+${cleanCode}` : '-';
  };

  const getRawCountryCode = (user) => {
    const code = user.usercountryCode || user.countryCode || '';
    return code.toString();
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString || dateString === '-' || dateString === 'null' || dateString === 'undefined') return '-';
    
    const cleanDate = dateString.toString().trim();
    
    if (cleanDate.includes('-') && cleanDate.split('-').length === 3) {
      const [year, month, day] = cleanDate.split('-');
      return `${day}/${month}/${year}`;
    }
    
    if (cleanDate.includes('/')) {
      return cleanDate;
    }
    
    try {
      const date = new Date(cleanDate);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.log('Date parsing error:', e);
    }
    
    return cleanDate;
  };

  const fetchAllUsersAndAdmins = async () => {
    try {
      const adminToken = localStorage.getItem('adminAuthToken');
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-user-and-admins', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to fetch users and admins');
      }

      if (data.userList && Array.isArray(data.userList)) {
        setAllUsers(data.userList);
      } else {
        throw new Error('Invalid user data format received');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const fetchAdminUsers = async () => {
    setAdminLoading(true);
    try {
      const adminToken = localStorage.getItem('adminAuthToken');
      const response = await fetch('https://car.amar.co.jp/api/authorization/admin-user/get-all-admin-superAdmin', {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${adminToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(data.message || 'Failed to fetch admin users');
      }

      if (data.userList && Array.isArray(data.userList)) {
        setAdminUsers(data.userList);
      } else if (data.adminList && Array.isArray(data.adminList)) {
        setAdminUsers(data.adminList);
      } else if (Array.isArray(data)) {
        setAdminUsers(data);
      } else if (data.data && Array.isArray(data.data)) {
        setAdminUsers(data.data);
      } else {
        setAdminUsers([]);
        showSnackbar('Unexpected response format for admin users', 'warning');
      }
    } catch (err) {
      showSnackbar(`Error fetching admin users: ${err.message}`, 'error');
      setAdminUsers([]);
    } finally {
      setAdminLoading(false);
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const token = localStorage.getItem('adminAuthToken');
        const res = await fetch('https://car.amar.co.jp/api/authorization/shipping/all-countries', {
          headers: { 'accept': 'application/json', 'Accept-Language': 'en', 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCountries(data);
        }
      } catch (err) {
        console.error('Fetch countries error:', err);
      }
    };

    const initializeFetch = async () => {
      try {
        await fetchAllUsersAndAdmins();
      } catch (err) {
        console.error('Initial fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
    initializeFetch();
    
    const interval = setInterval(async () => {
      try {
        await fetchAllUsersAndAdmins();
      } catch (err) {
        console.error('Polling fetch failed:', err);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleAdminFilterClick = () => {
    setActiveFilter('admin');
    if (adminUsers.length === 0) {
      fetchAdminUsers();
    }
  };

  const handleEditClick = (user) => {
    setEditingId(user.userId);
    const rawCode = getRawCountryCode(user);
    const matched = countries.find(c => c.countryPhoneCode === rawCode);
    setEditedUser({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      gender: user.gender || '',
      userEmailId: user.userEmailId || '',
      userMobileNumber: user.userMobileNumber || '',
      countryCode: rawCode,
      countryId: matched?.countryId || null,
      isAdmin: (user.roleName && (user.roleName.toLowerCase().includes('admin') || user.roleName.toLowerCase().includes('super'))) || 
               (user.role && (user.role.toLowerCase().includes('admin') || user.role.toLowerCase().includes('super')))
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedUser(null);
    setError(null);
  };

  const handleSaveClick = () => {
    // Validate mobile number if provided
    if (editedUser.userMobileNumber && editedUser.userMobileNumber.trim()) {
      const isValidMobile = validateMobileNumber(editedUser.userMobileNumber.trim());
      if (!isValidMobile) {
        setError('Mobile number must be between 10 and 15 digits (numbers only)');
        showSnackbar('Mobile number must be between 10 and 15 digits (numbers only)', 'error');
        return;
      }
    }
    
    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    setShowConfirmDialog(false);
    setIsSaving(true);
    setError(null);
    
    try {
      const adminToken = localStorage.getItem('adminAuthToken');
      
      if (!adminToken) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      const userData = {};
      
      if (editedUser.firstName && editedUser.firstName.trim()) {
        userData.firstName = editedUser.firstName.trim();
      }
      
      if (editedUser.lastName && editedUser.lastName.trim()) {
        userData.lastName = editedUser.lastName.trim();
      }
      
      if (editedUser.gender && editedUser.gender.trim()) {
        userData.gender = editedUser.gender.trim();
      }
      
      if (editedUser.userEmailId && editedUser.userEmailId.trim()) {
        userData.userEmailId = editedUser.userEmailId.trim();
      }
      
      if (editedUser.userMobileNumber && editedUser.userMobileNumber.trim()) {
        // Clean mobile number - keep only digits
        const cleanMobile = editedUser.userMobileNumber.trim().replace(/\D/g, '');
        userData.userMobileNumber = cleanMobile;
      }
      
      if (editedUser.countryId) {
        userData.countryId = editedUser.countryId;
      }

      // Determine which API endpoint to use based on user role
      let apiUrl;
      if (editedUser.isAdmin) {
        // Use admin update API
        apiUrl = `https://car.amar.co.jp/api/authorization/admin-user/update-admin-user/${editingId}`;
      } else {
        // Use regular user update API
        apiUrl = `https://car.amar.co.jp/api/authorization/admin-user/update-user-details-by-admin/${editingId}`;
      }

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const responseData = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminAuthToken');
          throw new Error('Session expired or invalid token. Please login again.');
        }
        throw new Error(responseData.message || `Failed to update user (Status: ${response.status})`);
      }

      if (responseData.code === 200 || response.ok) {
        const wasAdmin = editedUser.isAdmin;
        setEditingId(null);
        setEditedUser(null);
        showSnackbar(responseData.message || 'User updated successfully!', 'success');

        // Refresh both lists so table updates without page reload
        await fetchAllUsersAndAdmins();
        if (wasAdmin || activeFilter === 'admin') {
          await fetchAdminUsers();
        }
      } else {
        throw new Error(responseData.message || 'Unexpected response format');
      }
      
    } catch (err) {
      console.error('Save Error:', err);
      setError(err.message);
      
      if (err.message.includes('login again') || err.message.includes('Session expired')) {
        showSnackbar(`${err.message} - Redirecting to login page...`, 'error');
        setTimeout(() => {
          window.location.href = '/admin-login';
        }, 2000);
      } else {
        showSnackbar(`Error updating user: ${err.message}`, 'error');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    // For mobile number, only allow digits and limit to 15 digits
    if (field === 'userMobileNumber') {
      // Remove all non-digit characters
      const digitsOnly = value.replace(/\D/g, '');
      // Limit to 15 digits
      const limitedDigits = digitsOnly.slice(0, 15);
      
      setEditedUser(prev => ({
        ...prev,
        [field]: limitedDigits
      }));
    } else {
      setEditedUser(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDeleteClick = (userId) => {
    setUserToDelete(userId);
    setDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    setDeleteConfirmDialog(false);
    setIsDeleting(true);
    setError(null);
    
    try {
      const adminToken = localStorage.getItem('adminAuthToken');
      if (!adminToken) {
        throw new Error('Authentication token not found. Please login again.');
      }

      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-user/delete-user-by-userId/${userToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete user');
      }

      // Refresh the appropriate user list based on active filter
      if (activeFilter === 'admin') {
        await fetchAdminUsers();
      } else {
        await fetchAllUsersAndAdmins();
      }
      
      showSnackbar('User deleted successfully!', 'success');
      
    } catch (err) {
      setError(err.message);
      showSnackbar(`Error: ${err.message}`, 'error');
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmDialog(false);
    setUserToDelete(null);
  };

const fetchUserDetailsAndAddresses = async (userId) => {
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/authorization/admin-user/get-user-details/${userId}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en'
          }
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        
        if (responseData.success && responseData.data) {
          const userData = responseData.data;
          
          // Format user details
          const details = {
            firstName: userData.firstName || '-',
            lastName: userData.lastName || '-',
            email: userData.userEmailId || '-',
            userMobileNumber: userData.userMobileNumber || '-',
            countryCode: userData.countryCode ? formatCountryCode(userData.countryCode) : '-',
            gender: userData.gender || '-',
            dateOfBirth: userData.dateOfBirth || '-',
            roleId: userData.roleId || '-'
          };
          
          // Format addresses - map the correct field names from API
          const addresses = Array.isArray(userData.addresses) 
            ? userData.addresses.map(addr => ({
                addressId: addr.addressId || 'N/A',
                addressLine1: addr.addressLine1 || '-',
                addressLine2: addr.addressLine2 || '-',
                city: addr.city || '-',
                state: addr.state || '-',
                pincode: addr.pincode || '-',
                countryName: addr.countryName || '-',
                portName: addr.portName || '-',
                mobileNumber: addr.addressMobileNumber || '-',
                countryCode: addr.addressCountryCode || '',
                userId: userId
              }))
            : [];
          
          return { details, addresses };
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user details and addresses:', error.message);
      return null;
    }
  };

  const handleViewClick = async (user) => {
    // Mark new user as viewed
    if (user.isNewUser) {
      setViewedUserIds(prev => {
        const updated = new Set(prev);
        updated.add(user.userId);
        try {
          localStorage.setItem('viewedNewUserIds', JSON.stringify([...updated]));
        } catch {}
        return updated;
      });
    }
    setViewingUser(user);
    setViewDialog(true);
    setLoadingDetails(true);
    setUserDetails(null);
    setUserAddresses([]);

    try {
      const result = await fetchUserDetailsAndAddresses(user.userId);
      
      if (result && result.details) {
        setUserDetails(result.details);
        setUserAddresses(result.addresses || []);
        
        if (result.addresses && result.addresses.length > 0) {
          showSnackbar(`Loaded user details with ${result.addresses.length} address(es)`, 'success');
        } else {
          showSnackbar('User details loaded. No addresses found.', 'info');
        }
      } else {
        // Fallback to basic user information from the list
        const fallbackDetails = {
          firstName: user.firstName || '-',
          lastName: user.lastName || '-',
          email: user.userEmailId || '-',
          userMobileNumber: user.userMobileNumber || '-',
          countryCode: formatCountryCode(user.usercountryCode || user.countryCode),
          gender: user.gender || '-',
          dateOfBirth: user.dateOfBirth || '-',
          userName: user.userEmailId || '-',
          userCity: '-',
          userCountry: '-',
          roleId: '-'
        };
        
        setUserDetails(fallbackDetails);
        setUserAddresses([]);
        showSnackbar('Showing basic user information', 'warning');
      }

    } catch (err) {
      console.error('Error in handleViewClick:', err.message);
      showSnackbar('Failed to load user details: ' + err.message, 'error');
      
      // Set fallback data
      setUserDetails({
        firstName: user.firstName || '-',
        lastName: user.lastName || '-',
        email: user.userEmailId || '-',
        userMobileNumber: user.userMobileNumber || '-',
        countryCode: formatCountryCode(user.usercountryCode || user.countryCode),
        gender: user.gender || '-',
        dateOfBirth: user.dateOfBirth || '-',
        userName: user.userEmailId || '-',
        userCity: '-',
        userCountry: '-',
        roleId: '-'
      });
      setUserAddresses([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseViewDialog = () => {
    setViewDialog(false);
    setViewingUser(null);
    setUserDetails(null);
    setUserAddresses([]);
  };

  const getDisplayUsers = () => {
    if (activeFilter === 'admin') {
      // The backend's get-all-admin-superAdmin endpoint (adminUsers state)
      // predates the per-section admin roles (TICKET_ADMIN, INQUIRY_ADMIN,
      // etc.) and only returns ADMIN/SUPER_ADMIN — so derive this tab from
      // allUsers instead, same "any admin-ish role" rule already used for
      // the red highlight below.
      return allUsers.filter(user =>
        user.roleName && (user.roleName.toLowerCase().includes('admin') || user.roleName.toLowerCase().includes('super'))
      );
    } else if (activeFilter === 'user') {
      return allUsers.filter(user => 
        !user.roleName.toLowerCase().includes('admin') && 
        !user.roleName.toLowerCase().includes('super_admin')
      );
    }
    return allUsers;
  };

  const displayUsers = getDisplayUsers();

  const filteredUsers = displayUsers.filter(user => {
    const matchesSearch =
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userEmailId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.userMobileNumber && user.userMobileNumber.includes(searchTerm)) ||
      (user.countryCode && user.countryCode.includes(searchTerm)) ||
      (user.usercountryCode && user.usercountryCode.includes(searchTerm));

    return matchesSearch;
  }).sort((a, b) => {
    const aNew = a.isNewUser && !viewedUserIds.has(a.userId) ? 1 : 0;
    const bNew = b.isNewUser && !viewedUserIds.has(b.userId) ? 1 : 0;
    if (bNew !== aNew) return bNew - aNew; // new users first
    return b.userId - a.userId; // then descending userId
  });

  // Column sorting (click a header) — layered on top of filteredUsers;
  // falls back to its default new-first/userId order when no column is
  // actively sorted.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', fontSize: '10px', color: 'white' }}>▲▼</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: 'white' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortedFilteredUsers = !sortField ? filteredUsers : [...filteredUsers].sort((a, b) => {
    if (sortField === 'userId') {
      return sortDirection === 'asc' ? a.userId - b.userId : b.userId - a.userId;
    }
    const fieldMap = {
      name: (u) => `${u.firstName} ${u.lastName}`,
      gender: (u) => u.gender,
      email: (u) => u.userEmailId,
      countryCode: (u) => u.usercountryCode || u.countryCode,
      phone: (u) => u.userMobileNumber,
      role: (u) => u.roleName,
    };
    const aV = (fieldMap[sortField]?.(a) || '').toString().toLowerCase();
    const bV = (fieldMap[sortField]?.(b) || '').toString().toLowerCase();
    if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
    if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
       <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 2
        }}
      >
        <SpinningLogo src="/image/footer-logo-small.svg" alt="Loading" />
        <Typography variant="body1">Loading car data...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>
        <h3>Error: {error}</h3>
        {error.includes('login') && (
          <button
            onClick={() => window.location.href = '/admin-login'}
            style={{ marginTop: '20px', padding: '10px 20px', cursor: 'pointer' }}
          >
            Go to Login
          </button>
        )}
      </div>
    );
  }

  const adminTabCount = allUsers.filter(user =>
    user.roleName && (user.roleName.toLowerCase().includes('admin') || user.roleName.toLowerCase().includes('super'))
  ).length;
  const regularTabCount = allUsers.length - adminTabCount;

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      {snackbar.show && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          left: '50%',
          transform: 'translateX(-50%)',
          minWidth: '320px',
          maxWidth: '500px',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 10000,
          borderLeft: `4px solid ${snackbar.type === 'success' ? '#10b981' : snackbar.type === 'error' ? '#ef4444' : snackbar.type === 'warning' ? '#f59e0b' : '#048300ff'}`,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px'
        }}>
          <span style={{ flex: 1, color: '#1f2937', fontSize: '14px' }}>{snackbar.message}</span>
          <button 
            onClick={() => setSnackbar({ show: false, message: '', type: 'success' })}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#6b7280' }}
          >
            ×
          </button>
        </div>
      )}

      {showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '400px' }}>
            <h3>Confirm Update</h3>
            <p>Are you sure you want to modify this user?</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                disabled={isSaving}
                style={{ flex: 1, padding: '10px', cursor: 'pointer' }}
              >
                No
              </button>
              <button
                onClick={handleConfirmSave}
                disabled={isSaving}
                style={{ flex: 1, padding: '10px', cursor: 'pointer', background: '#EB2329', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                {isSaving ? 'Saving...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '24px', borderRadius: '8px', maxWidth: '400px' }}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to remove this user?</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                style={{ flex: 1, padding: '10px', cursor: 'pointer' }}
              >
                No
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                style={{ flex: 1, padding: '10px', cursor: 'pointer', background: '#EB2329', color: 'white', border: 'none', borderRadius: '4px' }}
              >
                {isDeleting ? 'Deleting...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #EB2329 0%, #c41f23 100%)',
              color: 'white',
              padding: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px' }}>User Details</h2>
              <button
                onClick={handleCloseViewDialog}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: 'white'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
              {loadingDetails ? (
                <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                  <div>Loading user details...</div>
                </div>
              ) : (
                <>
                  <div style={{
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    padding: '20px',
                    marginBottom: '20px',
                    borderLeft: '4px solid #EB2329'
                  }}>
                    <h3 style={{ color: '#EB2329', fontSize: '18px', marginTop: 0 }}>
                      Profile Information
                    </h3>
                    {userDetails ? (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>First Name:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.firstName || '-'}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Last Name:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.lastName || '-'}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Email:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.email || '-'}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Mobile Number:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.userMobileNumber || '-'}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Country Code:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.countryCode ? (userDetails.countryCode.toString().startsWith('+') ? userDetails.countryCode : `+${userDetails.countryCode}`) : '-'}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Date of Birth:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {formatDateDisplay(userDetails.dateOfBirth)}
                          </div>
                        </div>
                        <div style={{ background: 'white', padding: '12px', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
                          <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, textTransform: 'uppercase' }}>Gender:</div>
                          <div style={{ fontSize: '15px', color: '#1f2937', fontWeight: 500 }}>
                            {userDetails.gender || '-'}
                          </div>
                        </div>
                      
                     
                      
                      
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '2px dashed #e5e7eb'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>👤</div>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '15px', fontWeight: 500 }}>
                          No profile details available
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={{
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    padding: '20px',
                    borderLeft: '4px solid #EB2329'
                  }}>
                    <h3 style={{ color: '#EB2329', fontSize: '18px', marginTop: 0 }}>
                      Saved Addresses ({userAddresses.length})
                    </h3>
                    {userAddresses.length > 0 ? (
                      <div style={{ display: 'grid', gap: '16px' }}>
                        {userAddresses.map((address, index) => (
                          <div key={address.addressId || index} style={{
                            background: 'white',
                            borderRadius: '8px',
                            border: '2px solid #e5e5e5',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              background: 'linear-gradient(135deg, #EB2329 0%, #c41f23 100%)',
                              color: 'white',
                              padding: '12px 16px',
                              fontWeight: 600,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span>Address {index + 1}</span>
                            
                            </div>
                            <div style={{ padding: '16px' }}>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Address Line 1
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.addressLine1 || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Address Line 2
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.addressLine2 || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    City
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.city || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    State
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.state || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Pin Code
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.pincode || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Country
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.countryName || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Port
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.portName || '-'}
                                  </div>
                                </div>
                                
                                <div style={{ padding: '8px', background: '#f9fafb', borderRadius: '4px' }}>
                                  <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', marginBottom: '4px' }}>
                                    Contact Number
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#1f2937', fontWeight: 500 }}>
                                    {address.mobileNumber ? `${address.countryCode || ''} ${address.mobileNumber}`.trim() : '-'}
                                  </div>
                                </div>
                                
                              
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: '40px 20px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '2px dashed #e5e7eb'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '12px' }}>📍</div>
                        <p style={{ color: '#6b7280', margin: 0, fontSize: '15px', fontWeight: 500 }}>
                          No saved addresses found for this user
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <h2 style={{ margin: '0 0 20px 0', fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
        User Management
        <span style={{ display: 'block', width: 36, height: 3, background: '#e11d48', borderRadius: 2, marginTop: 6 }}></span>
      </h2>

      {/* -- TABS + SEARCH -- */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap', borderBottom: '2px solid #EBEBEB', marginBottom: '20px' }}>
        <div style={{ display: 'flex' }}>
          {[
            { key: 'all', label: 'All Users', count: allUsers.length },
            { key: 'admin', label: 'Admins', count: adminTabCount },
            { key: 'user', label: 'Regular Users', count: regularTabCount },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => (tab.key === 'admin' ? handleAdminFilterClick() : setActiveFilter(tab.key))}
              style={{
                padding: '12px 28px',
                border: 'none',
                borderBottom: activeFilter === tab.key ? '3px solid #d32f2f' : '3px solid transparent',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeFilter === tab.key ? '700' : '500',
                color: activeFilter === tab.key ? '#d32f2f' : '#666',
                marginBottom: '-2px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {tab.label}
              <span
                style={{
                  backgroundColor: activeFilter === tab.key ? '#d32f2f' : '#ddd',
                  color: activeFilter === tab.key ? 'white' : '#666',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: '600',
                }}
              >
                {tab.key === 'admin' && adminLoading ? '↻' : tab.count}
              </span>
            </button>
          ))}
        </div>

        <input
          type="text"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ width: '280px', maxWidth: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '10px' }}
        />
      </div>

      {filteredUsers.length > 0 ? (
        <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ backgroundColor: '#d32f2f', color: 'white' }}>
                <th onClick={() => handleSort('userId')} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>User ID{getSortIcon('userId')}</th>
                <th onClick={() => handleSort('name')} style={{ padding: '12px', textAlign: 'left', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Name{getSortIcon('name')}</th>
                <th onClick={() => handleSort('gender')} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Gender{getSortIcon('gender')}</th>
                <th onClick={() => handleSort('email')} style={{ padding: '12px', textAlign: 'left', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Email{getSortIcon('email')}</th>
                <th onClick={() => handleSort('countryCode')} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Country Code{getSortIcon('countryCode')}</th>
                <th onClick={() => handleSort('phone')} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Phone{getSortIcon('phone')}</th>
                <th onClick={() => handleSort('role')} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Role{getSortIcon('role')}</th>
                <th style={{ padding: '12px', textAlign: 'center', backgroundColor: '#d32f2f' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredUsers.map(user => (
                <tr
                  key={user.userId}
                  onClick={() => { if (editingId !== user.userId) handleViewClick(user); }}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    background: user.isNewUser && !viewedUserIds.has(user.userId) ? '#fefce8' : 'white',
                    transition: 'background 0.3s',
                    cursor: editingId === user.userId ? 'default' : 'pointer',
                  }}
                  onMouseEnter={e => { if (editingId !== user.userId) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (editingId !== user.userId) e.currentTarget.style.background = user.isNewUser && !viewedUserIds.has(user.userId) ? '#fefce8' : 'white'; }}
                >
                  <td style={{ padding: '12px', textAlign: 'center' }}>{user.userId}</td>
                  <td style={{ padding: '12px' }}>
                    {editingId === user.userId ? (
                      <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                        <input
                          type="text"
                          value={editedUser.firstName}
                          onChange={(e) => handleFieldChange('firstName', e.target.value)}
                          placeholder="First Name"
                          style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <input
                          type="text"
                          value={editedUser.lastName}
                          onChange={(e) => handleFieldChange('lastName', e.target.value)}
                          placeholder="Last Name"
                          style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                      </div>
                    ) : (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {`${user.firstName} ${user.lastName}`}
                        {user.isNewUser && !viewedUserIds.has(user.userId) && (
                          <span style={{
                            background: '#16a34a',
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: '700',
                            padding: '1px 7px',
                            borderRadius: '10px',
                            letterSpacing: '0.5px',
                            textTransform: 'uppercase'
                          }}>NEW</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {editingId === user.userId ? (
                      <select
                        value={editedUser.gender || ''}
                        onChange={(e) => handleFieldChange('gender', e.target.value)}
                        style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    ) : (
                      user.gender || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {editingId === user.userId ? (
                      <input
                        type="email"
                        value={editedUser.userEmailId}
                        onChange={(e) => handleFieldChange('userEmailId', e.target.value)}
                        placeholder="email@example.com"
                        style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px', width: '100%' }}
                      />
                    ) : (
                      user.userEmailId
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {editingId === user.userId ? (() => {
                      const selectedCountry = countries.find(c => c.countryPhoneCode === editedUser.countryCode);
                      return (
                        <div style={{ position: 'relative' }}>
                          <select
                            value={editedUser.countryCode || ''}
                            onChange={(e) => {
                              const selectedCode = e.target.value;
                              const matched = countries.find(c => c.countryPhoneCode === selectedCode);
                              handleFieldChange('countryCode', selectedCode);
                              handleFieldChange('countryId', matched?.countryId || null);
                            }}
                            style={{
                              padding: '6px', border: '1px solid #ddd',
                              borderRadius: '4px', maxWidth: '150px',
                              appearance: 'auto'
                            }}
                          >
                            <option value="">Select</option>
                            {countries.map((country) => (
                              <option key={country.countryId} value={country.countryPhoneCode}>
                                {country.isoCode} {country.countryPhoneCode} - {country.countryName}
                              </option>
                            ))}
                          </select>
                          {selectedCountry && (
                            <div style={{
                              position: 'absolute', top: 0, left: 0,
                              width: '100%', height: '100%',
                              display: 'flex', alignItems: 'center',
                              paddingLeft: '6px',
                              pointerEvents: 'none',
                              background: 'white',
                              borderRadius: '4px',
                              fontSize: '13px',
                              color: '#333',
                              border: '1px solid #ddd',
                              boxSizing: 'border-box',
                              maxWidth: '150px',
                            }}>
                              {selectedCountry.isoCode} {selectedCountry.countryPhoneCode}
                              <span style={{ marginLeft: 'auto', marginRight: '6px', color: '#666' }}>▾</span>
                            </div>
                          )}
                        </div>
                      );
                    })() : (
                      formatCountryCode(user.usercountryCode || user.countryCode)
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    {editingId === user.userId ? (
                      <div style={{ position: 'relative' }}>
                        <input
                          type="tel"
                          value={editedUser.userMobileNumber || ''}
                          onChange={(e) => handleFieldChange('userMobileNumber', e.target.value)}
                          placeholder="1234567890"
                          style={{ 
                            padding: '6px', 
                            border: error && editedUser.userMobileNumber && !validateMobileNumber(editedUser.userMobileNumber) ? '1px solid #ef4444' : '1px solid #ddd', 
                            borderRadius: '4px', 
                            width: '100%' 
                          }}
                          maxLength="15"
                          pattern="[0-9]*"
                          inputMode="numeric"
                        />
                       
                      </div>
                    ) : (
                      user.userMobileNumber || '-'
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      background: (user.roleName && (user.roleName.toLowerCase().includes('admin') || user.roleName.toLowerCase().includes('super'))) || (user.role && (user.role.toLowerCase().includes('admin') || user.role.toLowerCase().includes('super'))) ? '#fee2e2' : '#dbeafe',
                      color: (user.roleName && (user.roleName.toLowerCase().includes('admin') || user.roleName.toLowerCase().includes('super'))) || (user.role && (user.role.toLowerCase().includes('admin') || user.role.toLowerCase().includes('super'))) ? '#991b1b' : '#1e40af'
                    }}>
                      {user.roleName || user.role || 'User'}
                    </span>
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleViewClick(user); }}
                        title="View"
                        style={{
                          padding: '6px 12px',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        View
                      </button>
                      {editingId === user.userId ? (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSaveClick(); }}
                            title="Save"
                            disabled={editedUser.userMobileNumber && !validateMobileNumber(editedUser.userMobileNumber)}
                            style={{
                              padding: '6px 12px',
                              background: editedUser.userMobileNumber && !validateMobileNumber(editedUser.userMobileNumber) ? '#9ca3af' : '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: editedUser.userMobileNumber && !validateMobileNumber(editedUser.userMobileNumber) ? 'not-allowed' : 'pointer'
                            }}
                          >
                            Save
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                            title="Cancel"
                            style={{
                              padding: '6px 12px',
                              background: '#6b7280',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditClick(user); }}
                          title="Edit"
                          style={{
                            padding: '6px 12px',
                            background: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClick(user.userId); }}
                        title="Delete"
                        style={{
                          padding: '6px 12px',
                          background: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <h3>No users found</h3>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      <div style={{ marginTop: '20px', color: '#6b7280', fontSize: '14px' }}>
        Showing {filteredUsers.length} of {displayUsers.length} {activeFilter === 'admin' ? 'admins' : activeFilter === 'user' ? 'users' : 'users and admins'}
      </div>
    </div>
  );
};

export default AllUsers;